import React, { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  ColumnFiltersState,
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  Row,
  useReactTable,
} from '@tanstack/react-table';

import {
  getDefaultExpandedState,
  nestAdBoardDataFeedByThread,
} from '../../../utils/functions/format-feed-data';
import i18n from '../../../utils/i18n';
import {
  AdBoardGeneralDiscussionDto,
  AdBoardGeneralDiscussionDtoWithSubRows,
  AdBoardOutletContextType,
} from '../types';

import { FeedContentCell } from './FeedContentCell';
import { ParticipantNameCell } from './ParticipantNameCell';
import { SubRowCell } from './SubRowCell';

const createColumns = (filterByUserId?: string, selectedMessageId?: string) => {
  const columnHelper =
    createColumnHelper<AdBoardGeneralDiscussionDtoWithSubRows>();
  return [
    columnHelper.accessor('userId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'User id',
      // Custom filter for userId column
      filterFn: (row, columnId, filterValue) => {
        const userId = row.getValue(columnId);
        return userId === Number(filterValue); // Compare after converting filterValue to a number
      },
    }),
    columnHelper.accessor('participantName', {
      cell: (info) => {
        return (
          info.row.depth < 1 && (
            <ParticipantNameCell
              filterByUserId={!!filterByUserId}
              participantName={info.getValue()}
              createdAt={info.row.original.createdAt.toString()}
              userId={info.row.original?.userId?.toString()}
            />
          )
        );
      },
      size: 160,
      header: 'Participant name',
    }),
    columnHelper.accessor('content', {
      cell: (info) => {
        const commonProps = {
          context: info.row.original.messageContext,
          content: info.getValue(),
          editedAt: info.row.original.editedAt,
          hidden: info.row.original.hidden,
          // Following 3 needed for participant view and modal view of the table
          threadId: info.row.original.threadId,
          messageId: info.row.original.messageId,
          parentMessageContent: info.row.original.parentMessageContent,
          getCanExpand: info.row.getCanExpand,
          getIsExpanded: info.row.getIsExpanded,
          toggleExpanded: info.row.toggleExpanded,
          subRowCount: info.row.original.subRows?.length ?? 0,
          // All 4 needed for participant view and modal view of the table
          selectedMessageId,
          viewThreadModal: !!selectedMessageId,
          participantView: !!filterByUserId,
          isGeneralDiscussion: true,
        };

        return info.row.depth > 0 ? (
          <SubRowCell
            participantName={
              info.row.depth > 0
                ? info.row.original.participantName
                : info.getValue() ?? ''
            }
            createdAt={info.row.original.createdAt.toString()}
            userId={info.row.original?.userId?.toString()}
            {...commonProps}
          />
        ) : (
          <FeedContentCell {...commonProps} />
        );
      },
      header: 'Content',
    }),
    columnHelper.accessor('adminId', {
      cell: (info) => info.getValue(),
      header: 'Admin id',
    }),
    columnHelper.accessor('editedAt', {
      cell: (info) => info.getValue(),
      header: 'Edited at',
    }),
    columnHelper.accessor('hidden', {
      cell: (info) => info.getValue(),
      header: 'Hidden',
    }),
    columnHelper.accessor('messageId', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Message id',
    }),
    columnHelper.accessor('parentMessageContent', {
      cell: (info) => <Box>{info.getValue()}</Box>,
      header: 'Parent Message Content',
    }),
  ];
};

const globalFilterFn = (
  row: Row<AdBoardGeneralDiscussionDto>,
  columnId: string,
  filterValue: string
): boolean => {
  const { participantName, content } = row.original;

  const lowerCaseFilterValue = filterValue.toLowerCase();

  return (
    participantName.toLowerCase().includes(lowerCaseFilterValue) ||
    (content?.toLowerCase().includes(lowerCaseFilterValue) ?? false)
  );
};

export const GeneralDiscussionTable = ({
  searchQuery,
  filterByUserId,
  selectedMessageId,
}: {
  searchQuery?: string;
  filterByUserId?: string;
  selectedMessageId?: string;
}) => {
  const tableRef = useRef<HTMLDivElement | null>(null);

  const { generalDiscussionData } =
    useOutletContext<AdBoardOutletContextType>();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const [feedDataWithSubrows, setFeedDataWithSubrows] = React.useState<
    AdBoardGeneralDiscussionDtoWithSubRows[]
  >([]);

  useEffect(() => {
    const nested = nestAdBoardDataFeedByThread(
      generalDiscussionData,
      !!filterByUserId
    );

    let filtered = nested;

    // this case happens for participant view and threaded message modal
    if (selectedMessageId) {
      // react table does not do well the filtering -> we need pre filtering
      // on click on a parent msg, we need all the subrows
      // on click on the children msg, we need to see parent and children msgs
      // filter to include all messages in the thread, not just the selected message id
      filtered = nested.filter((thread) => {
        const matchesParent = thread.messageId === selectedMessageId;
        const matchesChild = thread.subRows?.some(
          (sub) => sub.messageId === selectedMessageId
        );
        return matchesParent || matchesChild;
      });
    }

    const defaultExpanded = getDefaultExpandedState(
      nested,
      !!filterByUserId,
      !!selectedMessageId
    );

    setFeedDataWithSubrows(filtered);
    setExpanded(defaultExpanded);
  }, [generalDiscussionData, filterByUserId, selectedMessageId]);

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );

  useEffect(() => {
    const filters = [];
    if (filterByUserId)
      filters.push({
        id: 'userId',
        value: filterByUserId,
      });

    setColumnFilters(filters);
  }, [filterByUserId]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const columns = createColumns(filterByUserId, selectedMessageId);
  // Use the table hook with global filter enabled
  const table = useReactTable({
    data: feedDataWithSubrows,
    columns,
    state: {
      globalFilter: searchQuery,
      columnFilters,
      columnVisibility: {
        userId: false,
        adminId: false,
        editedAt: false,
        hidden: false,
        messageId: false,
        parentMessageContent: false,
      },
      pagination,
      expanded, // use row id to see if true or false and decide whether to expand it or not
    },
    globalFilterFn,
    onExpandedChange: setExpanded,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.messageId,
    getSubRows: (row) => row.subRows,
    // do not show other people's messages in participant view, even if the participant has a reply in their thread
    filterFromLeafRows: filterByUserId ? false : true,
  });

  return (
    <TableContainer
      ref={tableRef}
      bg={useColorModeValue('white', 'gray.700')}
      mt={4}
      maxH={'100%'}
      overflowY={'scroll'}
    >
      <Table variant="unstyled" data-testid="feed-table">
        <Tbody>
          {table.getExpandedRowModel().rows?.length ? (
            table.getExpandedRowModel().rows.map((row) => (
              <Tr
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                fontSize={'sm'}
              >
                {row.getVisibleCells().map((cell) => (
                  <Td
                    key={cell.id}
                    textAlign="left"
                    verticalAlign="top"
                    style={{
                      paddingLeft: '18px',
                      paddingRight: '18px',
                      paddingTop: cell.row.depth < 1 ? '16px' : '8px',
                      paddingBottom: cell.row.depth < 1 ? '16px' : '8px',
                      whiteSpace: 'pre-wrap',
                      ...(cell.column.id === 'participantName'
                        ? {
                            width: `${cell.column.getSize()}px`,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }
                        : {}),
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))
          ) : (
            <Tr>
              <Td
                colSpan={columns.length}
                textAlign={'center'}
                color={'blue.700'}
                py={20}
              >
                {i18n.t('adBoardDetails.engagement.noData')}
              </Td>
            </Tr>
          )}
        </Tbody>
      </Table>
    </TableContainer>
  );
};
